/**
 * InlineChat Component
 *
 * Compact inline chat interface that appears below transcript segments.
 * Handles message display, input, and streaming responses for segment-level
 * discussions with the AI therapist.
 */

import { useEffect, useRef, useState } from "react";
import { shouldSendOnEnter } from "../../lib/composerKeys";
import { useStickyScroll } from "../../hooks/useStickyScroll";
import { Loader2, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { StreamingChatMessage } from "./StreamingChatMessage";
import {
  useSegmentChat,
  useCreateSegmentChat,
  useStreamingSegmentChat,
} from "@/hooks/useStreamingChat";

interface InlineChatProps {
  transcriptId: string;
  segmentId: string;
  onClose: () => void;
  /** Callback when message count changes */
  onMessageCountChange?: (count: number) => void;
}

export function InlineChat({
  transcriptId,
  segmentId,
  onClose,
  onMessageCountChange,
}: InlineChatProps) {
  const [input, setInput] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Fetch or create chat
  const { data: chat, isLoading } = useSegmentChat(
    transcriptId,
    segmentId,
    true
  );

  // Create chat mutation (for first message if chat doesn't exist)
  const createChat = useCreateSegmentChat(transcriptId, segmentId);

  // Streaming state
  const { streamingState, sendMessage, resetStreaming } =
    useStreamingSegmentChat(transcriptId, segmentId);

  // Report message count changes to parent
  useEffect(() => {
    const messageCount = chat?.messages?.length ?? 0;
    onMessageCountChange?.(messageCount);
  }, [chat?.messages?.length, onMessageCountChange]);

  // Follow the stream only while the user is already at the bottom. This used
  // to scroll unconditionally, so reading back mid-response was impossible.
  const { containerRef, handleScroll, scrollToBottom } = useStickyScroll([
    chat?.messages,
    streamingState.content,
    streamingState.pendingUserMessage,
  ]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Reset streaming state when component unmounts or segment changes
  useEffect(() => {
    return () => {
      resetStreaming();
    };
  }, [segmentId, resetStreaming]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || streamingState.isStreaming) return;

    const userMessage = input.trim();
    setInput("");
    // The hook now tracks pendingUserMessage internally via streamingState

    try {
      // Create chat if it doesn't exist yet
      if (!chat) {
        await createChat.mutateAsync(undefined);
      }

      // Send message and stream response
      // The hook will set pendingUserMessage before streaming starts
      await sendMessage({ content: userMessage });

      // Sending is an explicit action, so re-pin to the live edge.
      scrollToBottom();
    } catch (err) {
      console.error("Failed to send message:", err);
      // Restore input on error
      setInput(userMessage);
      toast.error("Failed to send message. Please try again.");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (shouldSendOnEnter(e)) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const hasMessages = chat && chat.messages.length > 0;

  return (
    <div
      className="mt-3 border border-border rounded-lg bg-background shadow-sm overflow-hidden"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-muted/30 border-b border-border">
        <span className="text-xs font-medium text-muted-foreground">
          Discuss this segment
        </span>
        <button
          type="button"
          onClick={onClose}
          className="p-1 hover:bg-muted rounded text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Messages */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="max-h-64 overflow-y-auto p-3"
      >
        {isLoading && !chat ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : !hasMessages &&
          !streamingState.content &&
          !streamingState.pendingUserMessage ? (
          <p className="text-xs text-muted-foreground text-center py-4">
            Ask questions or get therapist guidance about this moment.
          </p>
        ) : (
          <div className="space-y-3">
            {/* Saved messages */}
            {chat?.messages.map((message) => (
              <StreamingChatMessage
                key={message.id}
                message={message}
                compact
              />
            ))}

            {/* Pending user message (optimistic UI) */}
            {streamingState.pendingUserMessage && (
              <StreamingChatMessage
                role="user"
                streamingContent={streamingState.pendingUserMessage}
                compact
              />
            )}

            {/* Loading indicator while waiting for streaming */}
            {streamingState.isStreaming && streamingState.content === "" && (
              <div className="flex items-start gap-2">
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-purple-600 dark:bg-purple-700 text-white flex items-center justify-center">
                  <span className="text-xs font-medium">T</span>
                </div>
                <div className="px-3 py-2 rounded-lg bg-muted text-sm">
                  <Loader2 className="h-3 w-3 animate-spin" />
                </div>
              </div>
            )}

            {/* Streaming message */}
            {streamingState.isStreaming && streamingState.content && (
              <StreamingChatMessage
                isStreaming={true}
                streamingContent={streamingState.content}
                role="assistant"
                compact
              />
            )}

            {/* Streaming error */}
            {streamingState.error && (
              <p className="text-xs text-destructive text-center">
                Error: {streamingState.error}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Input form */}
      <form onSubmit={handleSubmit} className="border-t border-border p-2">
        <div className="flex gap-2">
          <Textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask about this moment..."
            className="flex-1 min-h-[60px] max-h-[100px] resize-none text-sm"
            disabled={streamingState.isStreaming}
          />
          <Button
            type="submit"
            size="sm"
            disabled={!input.trim() || streamingState.isStreaming}
            className="self-end"
          >
            {streamingState.isStreaming ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Send className="h-3.5 w-3.5" />
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}
